import styled, { x } from '@xstyled/styled-components';
import React, {
  Children,
  Component,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { t } from 'ttag';
import { Item, registry, RenderProps } from '../Item';
import { AUDIO, HORIZONTAL_AUDIO_PLAYER, IMAGE, IPAD, VIDEO } from '../itemIds';
import LlamaIcon from '../../../../../assets/icons/smLlamaIcon.png';
import IpadImage from './Ipad.png';
import ArrowAndDots from './arrow_and_dots.png';
import { TrainingState, useTrainingStore } from '~/training/state';
import { Logo } from '~/ui/logo/Logo';
import { select } from '~/lib/select';

const Flex = styled.divBox`
  display: flex;
  box-sizing: border-box;
  pointer-events: none;
  > * {
    pointer-events: all;
  }
  height: 92vh;
  min-height: 600px;
  justify-content: center;
  aspect-ratio: 3 / 4;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: sm) {
    height: unset;
    min-height: unset;
  }
`;

const IpadBackground = styled.divBox`
  display: block;
  height: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-image: url(${IpadImage as string});
  background-position: center top;
  padding-left: 40px;
  padding-right: 40px;
  padding-top: 9%;
  overflow: hidden;
  box-sizing: border-box;
  .fromToSection {
    margin-left: 20px;
    color: #000;
    display: flow-root;
    span {
      color: accentDark;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
      font-family: sans-serif;
      b {
        font-family: unset;
        color: #000;
        font-weight: 500;
        margin-left: 14px;
      }
    }
    p {
      font-family: var(--body-font);
      strong {
        color: #6f6f6f;
        font-variant-numeric: lining-nums proportional-nums;
        font-family: Raleway;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
      }
    }
  }
  @media (max-width: sm) {
    height: auto;
    background-image: unset;
    background-color: #f5f5f5;
    border: 14px solid;
    border-radius: 20px;
    padding: 3px;
    overflow: visible;
    .ipadBody {
      color: #000;
    }
  }
`;

interface Props {
  from: string;
  subject: string;
  title?: string;
  body?: string;
  logo?: string;
}

const LogoIcon = styled.divBox`
  width: 60px;
  height: 60px;
  object-fit: contain;
  background-color: ${p => p.theme.colors.accentDark};
  border-radius: 50%;
  float: left;
  margin-right: 12px;
  background-image: url(${LlamaIcon});
  background-repeat: no-repeat;
  background-size: 45%;
  background-position: center;
  img {
    border-radius: 50%;
  }
`;

/* Scrollable email content (from/to + body). Uses max-height so short
   content stays compact, and long content scrolls inside the iPad screen. */
const ScrollArea = styled.divBox`
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`;

const Body = styled.divBox`
  color: #000;
  font-variant-numeric: lining-nums proportional-nums;
  font-family: Raleway;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
  padding-left: 20px;
  padding-right: 15px;

  & > p:first-child {
    position: relative;
    box-sizing: border-box;
    padding-right: 72px;
    min-height: 26px;

    &::after {
      content: '';
      position: absolute;
      top: -2px;
      right: 0;
      width: 69px;
      height: 26px;
      background-image: url(${ArrowAndDots});
      background-repeat: no-repeat;
      background-size: 69px 26px;
      pointer-events: none;
    }
  }
`;

const Inbox = styled.divBox`
  margin-top: 25px;
  margin-left: 16px;
  margin-bottom: 10px;
  color: #000;
  font-family: sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 800;
  line-height: normal;
  span {
    border-radius: 5px;
    background: #dcdbdb;
    color: #1f2024;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    padding: 3px 8px;
    margin-left: 10px;
  }
`;

const Attachments = styled.divBox`
  margin-top: 20px;
  > b {
    font-size: 12px;
  }
  > div {
    float: left;
    width: 100%;
    height: 71px;
    margin-right: 18px;
    margin-top: 11px;
  }
  & .fileName {
    font-family: Raleway;
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 150%;
  }
`;

const selector = (s: TrainingState) => select(s, 'companyName', 'logoUrl');

export class Ipad extends Item<typeof IPAD, Props> {
  protected render({ children, title, body, logo }: RenderProps<Props>) {
    const { logoUrl } = useTrainingStore(selector);

    const childArray = Children.toArray(children);

    const [maxHeight, setMaxHeight] = useState('none');

    const refBg = useRef<HTMLDivElement | null>(null);
    const refTitle = useRef<HTMLDivElement | null>(null);
    const refScroll = useRef<HTMLDivElement | null>(null);

    let pTagsArray = body?.split(/<p>([\s\S]*?)<\/p>/);
    pTagsArray = pTagsArray?.filter(item => item.trim() !== '');

    const getFileName = (
      c: React.ReactElement<{ type?: string }>,
      i: number
    ) => {
      let fileExtension = '.jpeg';

      if (c?.props?.type == VIDEO) {
        fileExtension = '.mp4';
      }

      if (c?.props?.type == AUDIO) {
        fileExtension = '.mp3';
      }

      if (c?.props?.type == HORIZONTAL_AUDIO_PLAYER) {
        return null;
      }

      return (
        <span className="fileName">{`${c?.props?.type}${i}${fileExtension}`}</span>
      );
    };

    const handleResize = () => {
      const bg = refBg.current;
      const title = refTitle.current;
      if (!bg || !title) return;

      const bgHeight = bg.clientHeight;
      const titleHeight = title.clientHeight;

      if (bgHeight <= 0) return;

      const available = bgHeight - titleHeight - 110;
      setMaxHeight(`${Math.max(available, 0)}px`);
    };

    useLayoutEffect(() => {
      handleResize();

      const bg = refBg.current;
      let resizeObserver: ResizeObserver | undefined;
      if (typeof ResizeObserver !== 'undefined' && bg) {
        resizeObserver = new ResizeObserver(() => handleResize());
        resizeObserver.observe(bg);
      }

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver?.disconnect();
      };
    }, [body]);

    useEffect(() => {
      const raf = requestAnimationFrame(() => handleResize());
      return () => cancelAnimationFrame(raf);
    }, [body]);

    const attachmentsExist = childArray.find(c => {
      const child = c as React.ReactElement<{ type?: string }>;
      if (
        child.props.type == IMAGE ||
        child.props.type == AUDIO ||
        child.props.type == VIDEO ||
        child.props.type == HORIZONTAL_AUDIO_PLAYER
      ) {
        return true;
      }
    });

    return (
      <Flex className="nFlexDiv">
        <IpadBackground className="iPadBg" ref={refBg}>
          <div ref={refTitle}>
            <Inbox className="ipadTitle">
              {title}
              <span>{t`Inbox`}</span>
            </Inbox>
          </div>
          <ScrollArea
            className="ipadScrollArea"
            ref={refScroll}
            style={{ maxHeight }}
          >
            <div className="fromToSection">
              <LogoIcon
                style={
                  logo || logoUrl
                    ? { backgroundImage: 'unset', backgroundColor: 'unset' }
                    : {}
                }
              >
                {(logo || logoUrl) && (
                  <Logo
                    src={logo || logoUrl}
                    height={65}
                    width={65}
                    style={{ objectFit: 'contain' }}
                  />
                )}
              </LogoIcon>
              <x.p
                className="psec"
                dangerouslySetInnerHTML={{
                  __html: pTagsArray && pTagsArray[0] ? pTagsArray[0] : ''
                }}
              />
            </div>
            <Body className="ipadBody">
              {pTagsArray?.map((i, index) =>
                index === 0 ? (
                  false
                ) : (
                  <x.p
                    className={index == 1 ? 'firstP' : ''}
                    key={`P_${index}`}
                    dangerouslySetInnerHTML={{ __html: i }}
                  />
                )
              )}
              {childArray?.map((c: Component<RenderProps<void>>, i) => {
                if (
                  c.props.type == IMAGE ||
                  c.props.type == VIDEO ||
                  c.props.type == AUDIO ||
                  c.props.type == HORIZONTAL_AUDIO_PLAYER
                ) {
                  return false;
                }
                return <x.div key={i}>{c}</x.div>;
              })}
              {attachmentsExist && (
                <Attachments>
                  <x.b style={{ display: 'block', clear: 'both' }}>
                    {t`Attachments`}:
                  </x.b>
                  {childArray.map((c, i) => {
                    const child = c as React.ReactElement<{ type?: string }>;
                    if (
                      child.props.type != IMAGE &&
                      child.props.type != AUDIO &&
                      child.props.type != VIDEO &&
                      child.props.type != HORIZONTAL_AUDIO_PLAYER
                    ) {
                      return false;
                    }
                    return (
                      <x.div key={i}>
                        {c}
                        {getFileName(child, i)}
                      </x.div>
                    );
                  })}
                </Attachments>
              )}
            </Body>
          </ScrollArea>
        </IpadBackground>
      </Flex>
    );
  }
}

registry[IPAD] = Ipad;
